import { useState, useEffect, useMemo } from "react";
import { getMyBookings } from "../api/bookings.api";
import { getChat } from "../api/chat.api";
import { getStaffId } from "../utils/storage";
import { doctorDisplayName, messageSnippet } from "../utils/consultationChatFormat";

/**
 * @returns {{ conversations: import('../utils/consultationChatModels').Conversation[], loading: boolean, error: string|null, search: string, setSearch: (v: string) => void, refresh: () => void }}
 */
export function useDoctorConversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [tick, setTick] = useState(0);

  const refresh = () => setTick((t) => t + 1);

  useEffect(() => {
    const staffId = getStaffId();
    if (!staffId) {
      setError("Staff ID is not available.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getMyBookings(staffId);
        if (cancelled) return;

        if (!response.data.success || !response.data.bookings?.length) {
          setConversations([]);
          return;
        }

        /** @type {import('../utils/consultationChatModels').Doctor[]} */
        const doctors = response.data.bookings.reduce((acc, booking) => {
          const doctor = booking.doctorId || booking.doctor;
          if (doctor?._id && !acc.find((d) => d._id === doctor._id)) acc.push(doctor);
          return acc;
        }, []);

        // ponytail: one getChat per doctor for sidebar previews; fine for small lists
        const withPreviews = await Promise.all(
          doctors.map(async (doctor) => {
            try {
              const chatRes = await getChat(staffId, doctor._id);
              const messages = chatRes.data.success ? chatRes.data.messages || [] : [];
              const lastMessage = messages[messages.length - 1];
              const lastMessageAt = lastMessage?.timestamp ? new Date(lastMessage.timestamp) : null;
              return {
                doctor,
                lastMessage,
                lastMessageAt,
                snippet: messageSnippet(lastMessage),
              };
            } catch {
              return { doctor, lastMessage: undefined, lastMessageAt: null, snippet: "No messages yet" };
            }
          })
        );

        withPreviews.sort((a, b) => {
          const ta = a.lastMessageAt?.getTime() || 0;
          const tb = b.lastMessageAt?.getTime() || 0;
          return tb - ta;
        });

        if (!cancelled) setConversations(withPreviews);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Error fetching doctors.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const name = doctorDisplayName(c.doctor).toLowerCase();
      const spec = (c.doctor.specialization || "").toLowerCase();
      const snippet = (c.snippet || "").toLowerCase();
      return name.includes(q) || spec.includes(q) || snippet.includes(q);
    });
  }, [conversations, search]);

  return { conversations: filtered, loading, error, search, setSearch, refresh };
}
